import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { touchStaffPortalKind, applyStaffPortalDocument } from "@/lib/staffPortal";
import { hasTsSession } from "@/lib/tsIdentity";
import { isTeamHubHost, staffHomePath } from "@/lib/teamHub";
import StaffNavRail from "@/components/mobile/StaffNavRail";

const PINK = "#EE2677";

/**
 * TS app shell — wraps KA/DA/TI/CA/WE hubs with the global staff rail and
 * back navigation to staff home.
 *
 * Every staff app route goes through here (see TeamHubRoutes), which is what
 * makes this the one place the rail has to be mounted. The rail sits above each
 * app's own header, which is left exactly as it was: `hideBar` still hides the
 * app bar and does not touch the rail, because they answer different questions
 * ("where am I in the app" vs "which app am I in").
 */
export default function TsAppShell({ title, portionKind, hideBar = false, children }) {
  const navigate = useNavigate();
  // Delivery partners often have no TS JWT. "TS home" would dump them on the
  // TS login screen and feel like the Delivery session was wiped.
  const showTsHomeBack = hasTsSession();
  const homeLabel = isTeamHubHost() ? "Home" : "TS home";

  useEffect(() => {
    if (portionKind) {
      // Do not overwrite saved /pp/hub?token=… start URL — TS shell paths have no token.
      touchStaffPortalKind(portionKind);
      applyStaffPortalDocument(portionKind);
    }
  }, [portionKind]);

  return (
    <div className="ts-app-shell">
      <style>{`
        .ts-app-shell { min-height: 100dvh; display: flex; flex-direction: column; background: #F5F2EA; }
        .ts-app-shell-bar {
          display: flex; align-items: center; gap: 8px;
          padding: calc(10px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) 10px calc(12px + env(safe-area-inset-left, 0px));
          background: #fff; border-bottom: 1px solid #E5E7EB; position: sticky; top: 0; z-index: 40;
        }
        /* The rail is above the bar and owns the top safe-area inset, so the bar
           must not add it a second time. Written as an adjacent-sibling rule so
           it applies exactly when the rail actually rendered — it returns null
           for riders, embedded views and signed-out loads, and in those cases
           the bar is still the topmost element and keeps its own inset. */
        .staff-rail + .ts-app-shell-bar { padding-top: 10px; }
        .ts-app-shell-back {
          display: inline-flex; align-items: center; gap: 4px; border: none; background: transparent;
          color: ${PINK}; font-size: 14px; font-weight: 800; cursor: pointer; padding: 6px 4px;
        }
        .ts-app-shell-title { font-size: 15px; font-weight: 800; color: #2D2D2D; }
        .ts-app-shell-body { flex: 1; min-height: 0; display: flex; flex-direction: column; } .ts-app-shell-body > * { flex: 1; min-height: 0; }
      `}</style>
      <StaffNavRail />
      {!hideBar ? (
        <header className="ts-app-shell-bar">
          {showTsHomeBack ? (
            <button type="button" className="ts-app-shell-back" onClick={() => navigate(staffHomePath())}>
              <ArrowLeft size={18} aria-hidden />
              {homeLabel}
            </button>
          ) : null}
          {title ? <span className="ts-app-shell-title">{title}</span> : null}
        </header>
      ) : null}
      <div className="ts-app-shell-body">{children}</div>
    </div>
  );
}
